/**
 * LE RAIL À DEUX NIVEAUX — demande du fondateur, 21/09/2026.
 *
 * Vingt-cinq rubriques dans une liste plate ne se parcourent pas : on les lit
 * toutes pour en trouver une. Deux niveaux rendent la recherche visuelle
 * possible — l'œil saute de groupe en groupe, puis lit trois lignes.
 *
 * ⚠️ LES GROUPES NE RÉORDONNENT RIEN. L'ordre du rail porte déjà une règle —
 * « §7 CAPACITÉ ET REPRÉSENTATION, placée JUSTE AVANT les mutations, et l'ordre
 * du rail dit la règle » (rubriques.ts). Les groupes sont donc CONTIGUS dans
 * l'ordre existant : on ajoute des intertitres, on ne déplace aucune entrée.
 * Toute rubrique inconnue tombe dans le dernier groupe plutôt que de
 * disparaître — une entrée absente du rail est indiscernable d'un rail correct.
 *
 * ⚠️ CE FICHIER VIT DANS `partage` ET NON DANS `domaine` : l'étage `interface`
 * du Dockerfile ne copie que `frontend/` et `src/partage/`. Un import depuis
 * `src/domaine` compile en local et casse le build — défaut payé deux fois le
 * 21/09, et désormais tenu par `tests/frontiere-etage-interface.test.ts`.
 */

/** Un groupe : son intitulé, et les clés qu'il rassemble, dans l'ordre du rail. */
export const GROUPES_RAIL: ReadonlyArray<{ titre: string; cles: readonly string[] }> = [
  { titre: 'Instruire',   cles: ['bureau', 'instruction', 'guichet', 'flux', 'affaires'] },
  { titre: 'Territoire',  cles: ['carte'] },
  { titre: 'Droits',      cles: ['capacite', 'mutations', 'successions'] },
  { titre: 'Registre',    cles: ['territoire', 'foncier', 'attestations', 'livre', 'documents'] },
  { titre: 'Terrain',     cles: ['geometre', 'atelier', 'anomalies'] },
  { titre: 'Valeur',      cles: ['immobilier', 'coproprietes', 'fiscal'] },
  { titre: 'Ouverture',   cles: ['verify', 'courrier'] },
  { titre: 'Prospective', cles: ['prospective', 'jumeau', 'investment'] },
  { titre: 'Poste',       cles: ['comptes'] },
];

/**
 * Répartit les entrées VISIBLES du rail dans leurs groupes, en conservant
 * l'ordre reçu. Un groupe sans entrée visible n'est pas rendu : un intertitre
 * seul annoncerait un contenu qui n'existe pas dans ce mode.
 */
export function grouperLeRail<T extends { cle: string }>(
  entrees: readonly T[],
): Array<{ titre: string; entrees: T[] }> {
  const parCle = new Map<string, string>();
  for (const g of GROUPES_RAIL) for (const c of g.cles) parCle.set(c, g.titre);
  const dernier = GROUPES_RAIL[GROUPES_RAIL.length - 1]!.titre;

  const paquets = new Map<string, T[]>();
  for (const e of entrees) {
    // Une rubrique qu'aucun groupe ne nomme reste VISIBLE : elle tombe dans le
    // dernier groupe. Mieux vaut une entrée mal rangée qu'une entrée absente.
    const titre = parCle.get(e.cle) ?? dernier;
    const liste = paquets.get(titre);
    if (liste) liste.push(e); else paquets.set(titre, [e]);
  }
  return GROUPES_RAIL
    .map((g) => ({ titre: g.titre, entrees: paquets.get(g.titre) ?? [] }))
    .filter((g) => g.entrees.length > 0);
}
